import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { programsApi } from '../../../api/adminApi';
import './ProgramForm.css';

function ProgramForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [featureTimeline, setFeatureTimeline] = useState('');
  const [teacherRatio, setTeacherRatio] = useState('');
  const [icon, setIcon] = useState(null);
  const [photo, setPhoto] = useState(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;

    setLoading(true);

    programsApi
      .getOne(id)
      .then((data) => {
        setName(data?.name ?? data?.title ?? data?.programName ?? '');
        setDescription(data?.description ?? data?.desc ?? '');
        setAgeMin(data?.age_min != null ? String(data.age_min) : '');
        setAgeMax(data?.age_max != null ? String(data.age_max) : '');
        setFeatureTimeline(data?.feature_timeline ?? '');
        setTeacherRatio(data?.feature_ratio ?? '');
      })
      .catch((err) => setError(err.userMessage || 'Failed to load program'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    if (!description.trim()) {
      setError('Description is required.');
      return;
    }

    const ageMinNum = ageMin === '' ? NaN : Number(ageMin);

    if (Number.isNaN(ageMinNum) || ageMinNum < 0) {
      setError('Age min must be a number (0 or greater).');
      return;
    }

    if (!featureTimeline.trim()) {
      setError('Feature timeline is required.');
      return;
    }

    if (!teacherRatio.trim()) {
      setError('Teacher ratio is required.');
      return;
    }

    setSaving(true);

    const fields = {
      name: name.trim(),
      description: description.trim(),
      age_min: ageMinNum,
      age_max: ageMax === '' ? undefined : Number(ageMax),
      feature_timeline: featureTimeline.trim(),
      feature_ratio: teacherRatio.trim(),
      icon: icon instanceof File ? icon : null,
      photo: photo instanceof File ? photo : null,
    };

    const promise = isEdit
      ? programsApi.update(id, fields)
      : programsApi.create(fields);

    promise
      .then(() => navigate('/admin/programs', { replace: true }))
      .catch((err) => {
        setError(err.userMessage || 'Something went wrong.');
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <div className="admin-program-form-loading">Loading…</div>;
  }

  return (
    <div className="admin-program-form">
      <div className="admin-program-form-header">
        <Link to="/admin/programs" className="admin-program-form-back">
          ← Programs
        </Link>
        <h2>{isEdit ? 'Edit program' : 'New program'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="admin-program-form-fields">
        {error && (
          <div className="admin-program-form-error" role="alert">
            {error}
          </div>
        )}

        <div className="admin-program-form-field">
          <label htmlFor="program-name">Name *</label>
          <input
            id="program-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Preschool"
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-field">
          <label htmlFor="program-desc">Description *</label>
          <textarea
            id="program-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description of the program"
            rows={4}
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-row">
          <div className="admin-program-form-field">
            <label htmlFor="program-age-min">Age min *</label>
            <input
              id="program-age-min"
              type="number"
              min={0}
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              placeholder="0"
              disabled={saving}
            />
          </div>

          <div className="admin-program-form-field">
            <label htmlFor="program-age-max">Age max</label>
            <input
              id="program-age-max"
              type="number"
              min={0}
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              placeholder="Optional"
              disabled={saving}
            />
          </div>
        </div>

        <div className="admin-program-form-field">
          <label htmlFor="program-feature-timeline">
            Feature timeline *
          </label>
          <input
            id="program-feature-timeline"
            type="text"
            value={featureTimeline}
            onChange={(e) => setFeatureTimeline(e.target.value)}
            placeholder="e.g. 9am–3pm"
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-field">
          <label htmlFor="program-teacher-ratio">Teacher ratio *</label>
          <input
            id="program-teacher-ratio"
            type="text"
            value={teacherRatio}
            onChange={(e) => setTeacherRatio(e.target.value)}
            placeholder="e.g. 1:10"
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-field">
          <label htmlFor="program-icon">Icon (image)</label>
          <input
            id="program-icon"
            type="file"
            accept="image/*"
            onChange={(e) => setIcon(e.target.files?.[0] || null)}
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-field">
          <label htmlFor="program-photo">Photo (image)</label>
          <input
            id="program-photo"
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)}
            disabled={saving}
          />
        </div>

        <div className="admin-program-form-actions">
          <Link to="/admin/programs" className="admin-program-form-cancel">
            Cancel
          </Link>

          <button
            type="submit"
            className="admin-program-form-submit"
            disabled={saving}
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProgramForm;